import type { Mods } from '../sim/stats';
import { P } from '../world/player';
/* ============================================================
   被動池（依 VS 的設計邏輯重寫，2026-08-02）
   ============================================================

   每一項回傳的是**修正**而不是直接改數值 —— 見 sim/stats.ts 的說明。
   `mods(lv)` 給的是「這一項在第 lv 級時的總修正」，不是增量。

   ## 這次重寫改了什麼（設計依據：.claude/notes/upgrade-redesign.md §6）

   舊版八個被動**全部是 `+X%` 純加成**，所以沒有一個會改變「怎麼玩」，
   也就沒有記憶點。VS 的 17 個被動裡有一半是規則類（復活、經驗成長率、
   機率品質、風險槓桿），那才是「我在組一套東西」那個感覺的來源。

   新的池子分四類：

     數值類   對齊 VS 的 Might / Area / Duration / Cooldown / Amount
     衝擊軸   **我們與 VS 的差異點**，見下
     生存軸   結構、裝甲、回復
     規則類   復活（離散事件）、經驗成長（壓縮時間軸）、暴擊（改分布不改期望）

   ## 衝擊軸為什麼是「我們的」

   VS 只有兩條軸：清場與活著。我們多一條「打硬目標」——
   它接的是既有的 AC6 式踉蹌系統（sim/impact.ts）。

   關鍵性質是**它與密度反向**：清場 build 打不動 Boss 與精英，
   衝擊 build 面對雜魚海很慢。那個反向關係就是 build 分化。
   順帶讓手動鎖定（TAB）從「有也可以沒有也可以」變成一條 build 的核心操作。

   ## 刻意不做 Curse（VS 的自願增難度換獎勵）

   這個專案 2026-08-01 否決過同型的東西。玩家自願承擔的**常駐**懲罰
   很容易退化成「每次都選它，因為數學上划算」—— 那不是決策是儀式。
   要做的話應該做成**單次的賭注**（寶箱二選一）而不是常駐詞條。 */

export type Passive = {
  id: string; n: string; kind: string;
  /** 這一項動的是哪幾條軸。卡片上會顯示，讓玩家看得出「它跟我的武裝合不合」。 */
  axes: string[];
  d: (lv: number) => string;
  mods: (lv: number) => Mods;
  /** 不在 `P.s` 裡的效果（例如結構上限、復活次數）。取得該級時呼叫一次，給的是**增量**。 */
  onTake?: (lv: number) => void;
  /** 最高級。預設 5；規則類的離散效果刻意做得比較短。 */
  max?: number;
};

function pct(mul, lv) { return Math.round((Math.pow(mul, lv) - 1) * 100); }
function cut(mul, lv) { return Math.round((1 - Math.pow(mul, lv)) * 100); }

export var PASSIVES: Passive[] = [
  /* ── 數值類 ───────────────────────────────────────────────── */
  {
    id: 'p-power', n: '核心超載', kind: '被動', axes: ['傷害'],
    d: function (lv) { return '全武裝傷害 +' + pct(1.12, lv) + '%'; },
    mods: function (lv) {
      var m = Math.pow(1.12, lv);
      return { mul: { atkDmg: m, midDmg: m, ultDmg: m } };
    }
  },
  {
    /* 對齊 VS 的 Area。**我們原本整個沒有這條軸** ——
       而它是 VS 裡讓同一把武器「覆蓋形狀完全不同」的主要來源。 */
    id: 'p-area', n: '爆壓擴散', kind: '被動', axes: ['範圍'],
    d: function (lv) { return '所有爆炸與掃描範圍 +' + pct(1.14, lv) + '%'; },
    mods: function (lv) { return { mul: { area: Math.pow(1.14, lv) } }; }
  },
  {
    /* 對齊 VS 的 Duration。持續型武裝（旋刃、無人機）與大招吃它。 */
    id: 'p-dur', n: '延壽迴路', kind: '被動', axes: ['持續'],
    d: function (lv) { return '持續型效果的時間 +' + pct(1.16, lv) + '%'; },
    mods: function (lv) { return { mul: { dur: Math.pow(1.16, lv), ultDur: Math.pow(1.16, lv) } }; }
  },
  {
    id: 'p-time', n: '時間壓縮', kind: '被動', axes: ['冷卻'],
    d: function (lv) { return '冷卻 −' + cut(0.88, lv) + '%、射速 +' + pct(1.10, lv) + '%'; },
    mods: function (lv) {
      return { mul: { midCdMax: Math.pow(0.88, lv), ultCdMax: Math.pow(0.88, lv), atkRate: Math.pow(1.10, lv) } };
    }
  },
  {
    id: 'p-rate', n: '供彈加速', kind: '被動', axes: ['射速'],
    d: function (lv) { return '主武裝射速 +' + pct(1.13, lv) + '%'; },
    mods: function (lv) { return { mul: { atkRate: Math.pow(1.13, lv) } }; }
  },
  {
    /* 投射物數量。**5 級才 +1 發**是刻意的：多一發的價值遠高於百分比，
       如果每一級都給，這一項會變成唯一正解。VS 的 Duplicator 也是離散的。 */
    id: 'p-multi', n: '分導彈頭', kind: '被動', axes: ['彈數'],
    d: function (lv) {
      return lv < 5 ? ('主武裝彈丸散佈 +' + (lv * 6) + '%，滿級時多射一發')
        : '主武裝多射一發';
    },
    mods: function (lv) { return lv >= 5 ? { add: { atkCount: 1 } } : {}; }
  },

  /* ── 衝擊軸（我們與 VS 的差異點）─────────────────────────── */
  {
    /* 進化「穿甲協定」的鑰匙。被動同時是泛用加成與 build 走向的宣告 ——
       這是 VS 讓被動有記憶點的核心手法（17 個進化幾乎都指定一個被動）。 */
    id: 'p-impact', n: '動能穿甲', kind: '被動', axes: ['衝擊'],
    d: function (lv) { return '衝擊累積 +' + pct(1.22, lv) + '%（更快打出踉蹌）'; },
    mods: function (lv) { return { mul: { imp: Math.pow(1.22, lv) } }; }
  },
  {
    /* 進化「軌道支配」的鑰匙。 */
    id: 'p-stagger', n: '過載鎖定', kind: '被動', axes: ['衝擊'],
    d: function (lv) { return '踉蹌時間 +' + pct(1.16, lv) + '%、踉蹌期間傷害 +' + pct(1.10, lv) + '%'; },
    mods: function (lv) { return { mul: { stagT: Math.pow(1.16, lv), stagDmg: Math.pow(1.10, lv) } }; }
  },

  /* ── 生存軸 ───────────────────────────────────────────────── */
  {
    id: 'p-frame', n: '反應爐擴充', kind: '被動', axes: ['結構'],
    d: function (lv) { return '結構上限 +' + (lv * 18) + '、護盾上限 +' + (lv * 12); },
    /* 結構與護盾上限不在 P.s 裡（它們是 P.hpMax / P.shMax），
       所以走 onTake 的增量路徑而不是修正層。 */
    mods: function () { return {}; },
    onTake: function () {
      P.hpMax += 18; P.shMax += 12;
      P.hp = Math.min(P.hpMax, P.hp + 18); P.sh = Math.min(P.shMax, P.sh + 12);
    }
  },
  {
    /* **乘算不是減法。** 減法型的減傷疊到 100% 就無敵了，
       那是這類詞條最典型的死法。乘算滿級是 −30%，永遠到不了 0。 */
    id: 'p-armor', n: '複合裝甲', kind: '被動', axes: ['結構'],
    d: function (lv) { return '受到的傷害 −' + cut(0.93, lv) + '%'; },
    mods: function (lv) { return { mul: { armor: Math.pow(0.93, lv) } }; }
  },
  {
    /* **與護盾不同，它不要求脫離接觸**（見 sim/player.ts 的說明）：
       護盾是「拉開就回」的戰術資源，結構是「整場慢慢長」的策略資源。 */
    id: 'p-repair', n: '奈米修復', kind: '被動', axes: ['結構'],
    d: function (lv) { return '每秒回復 ' + (lv * 0.45).toFixed(2) + ' 點結構（不需脫離接觸）'; },
    mods: function (lv) { return { add: { regen: lv * 0.45 } }; }
  },

  /* ── 規則類（改的是「怎麼玩」不是數字大小）───────────────── */
  {
    /* 對應 VS 的 Tiragisú。**離散事件**：它不是連續加成，
       是「你有沒有第二條命」這個二元的事實，所以只有 3 級。 */
    id: 'p-revive', n: '緊急重啟模組', kind: '規則', axes: ['規則'], max: 3,
    d: function (lv) { return '緊急重啟次數 +' + lv + '（陣亡時原地復活並清場）'; },
    mods: function () { return {}; },
    onTake: function () { P.revives++; }
  },
  {
    /* 對應 VS 的 Crown。它不讓你變強，它讓你**更早變強** ——
       整場的節奏都被改寫，那是規則類的價值。 */
    id: 'p-growth', n: '資料同化', kind: '規則', axes: ['規則'],
    d: function (lv) { return '經驗獲得 +' + pct(1.14, lv) + '%（整場升級節奏提前）'; },
    mods: function (lv) { return { mul: { growth: Math.pow(1.14, lv) } }; }
  },
  {
    /* 暴擊改的是傷害的**分布**不是期望值 —— 它讓「這一發會不會爆」
       變成一件有感的事，這也是為什麼它跟純 +傷害 不能互相取代。 */
    id: 'p-crit', n: '臨界共振', kind: '被動', axes: ['傷害'],
    d: function (lv) { return '暴擊率 +' + (lv * 6) + '%、暴擊傷害 +' + (lv * 25) + '%'; },
    mods: function (lv) { return { add: { crit: lv * 0.06, critMul: lv * 0.25 } }; }
  },
  {
    id: 'p-magnet', n: '磁力場擴張', kind: '被動', axes: ['機動'],
    d: function (lv) { return '經驗球吸取範圍 +' + pct(1.4, lv) + '%'; },
    mods: function (lv) { return { mul: { magnet: Math.pow(1.4, lv) } }; }
  },
  {
    id: 'p-speed', n: '伺服增壓', kind: '被動', axes: ['機動'],
    d: function (lv) { return '移動速度 +' + pct(1.07, lv) + '%'; },
    mods: function (lv) { return { mul: { speed: Math.pow(1.07, lv) } }; }
  },

  /* ── 第二批：**改玩法，不是加數值**（2026-08-02）───────────────
     使用者的回饋是「池子選擇仍然太多加數值加倍率，改玩法的不多」。
     這六個一個百分比都沒有 —— 它們全部改的是**某個動作的意義**：

       受傷      從純損失      →  也是一次輸出        （過載排氣）
       鎖定      從輔助        →  可以連鎖的資源      （鎖定共鳴）
       踉蹌      從增傷窗口    →  一次爆炸            （踉蹌爆發）
       衝刺      從逃命        →  攻擊手段            （殘影）
       站住不動  從送死        →  砲台流的代價        （磁軌校準）
       擊殺      從清場        →  補給來源            （回收協定）

     效果本體在 `sim/rules.ts`（`mods` 表達不了「事件發生時做某件事」）。 */
  {
    id: 'p-vent', n: '過載排氣', kind: '規則', axes: ['規則', '衝擊'],
    d: function (lv) { return '**受到傷害時**朝四周排氣，造成 ' + (26 + lv * 16) + ' 傷害並擊退'; },
    mods: function () { return {}; }
  },
  {
    id: 'p-relay', n: '鎖定共鳴', kind: '規則', axes: ['規則', '衝擊'],
    d: function (lv) {
      return '**鎖定目標死亡時**，把 ' + Math.round(Math.min(1, 0.3 + lv * 0.14) * 100) +
        '% 的衝擊進度轉移給下一個鎖定目標';
    },
    mods: function () { return {}; }
  },
  {
    id: 'p-burst', n: '踉蹌爆發', kind: '規則', axes: ['規則', '衝擊'],
    d: function (lv) { return '敵人**踉蹌結束時爆炸**，造成 ' + (34 + lv * 26) + ' 範圍傷害'; },
    mods: function () { return {}; }
  },
  {
    id: 'p-afterimage', n: '殘影', kind: '規則', axes: ['規則', '機動'],
    d: function (lv) { return '**衝刺**在起點留下殘影，0.35 秒後爆炸造成 ' + (22 + lv * 15) + ' 傷害'; },
    mods: function () { return {}; }
  },
  {
    /* 砲台流。**它是一個真的取捨** —— 這個遊戲的核心壓力來自貼身，
       站住不動是最危險的事，所以這條規則的代價是玩家自己承擔的。 */
    id: 'p-anchor', n: '磁軌校準', kind: '規則', axes: ['規則', '傷害'],
    d: function (lv) { return '**靜止 1.2 秒後**，全武裝傷害 ×' + (1 + lv * 0.22).toFixed(2) + '（一移動就失效）'; },
    mods: function () { return {}; }
  },
  {
    id: 'p-salvage', n: '回收協定', kind: '規則', axes: ['規則'],
    d: function (lv) { return '擊殺時有 ' + Math.round((0.02 + lv * 0.018) * 100) + '% 機率**掉落修復包**'; },
    mods: function () { return {}; }
  },
  /* ── 推進器四條軸（使用者拍板的四個成長方向）─────────────────
     加軸原則見 `world/player.ts` 的註解：**每一條都要有人動它**。
     這四條就是動 thrustMax／thrustRise／slam／airDmg 的那些人。 */
  {
    id: 'p-luck', n: '機率補償器', kind: '規則', axes: ['規則'],
    /* 幸運只影響**寶箱開出幾件**（`sim/chest.ts` 的拉霸），完全不碰傷害。

       它是這個池子裡唯一一條「投資運氣」的軸：拿了它，
       每一次穿過敵群去撿那個箱子的期望報酬都變高。
       VS 的 Luck 也是這個位置 —— 不改變你打得多痛，改變你抽到什麼。 */
    d: function (lv) { return '寶箱開出多件的機率 +' + pct(1.18, lv) + '%'; },
    mods: function (lv) { return { mul: { luck: Math.pow(1.18, lv) } }; }
  },
  {
    id: 'p-fuel', n: '增壓燃槽', kind: '被動', axes: ['機動'],
    d: function (lv) { return '推進能量上限 +' + pct(1.16, lv) + '%、回充 +' + pct(1.12, lv) + '%'; },
    mods: function (lv) { return { mul: { thrustMax: Math.pow(1.16, lv), thrustRegen: Math.pow(1.12, lv) } }; }
  },
  {
    id: 'p-vector', n: '向量噴口', kind: '被動', axes: ['機動'],
    d: function (lv) { return '上升速度 +' + pct(1.15, lv) + '%、空中轉向更利落'; },
    mods: function (lv) { return { mul: { thrustRise: Math.pow(1.15, lv) } }; }
  },
  {
    /* 落地衝擊是飛行唯一的攻擊出口，所以它自己就是一條 build 線。 */
    id: 'p-slam', n: '重錘落地', kind: '被動', axes: ['機動', '傷害'],
    d: function (lv) { return '落地衝擊傷害 +' + pct(1.34, lv) + '%、範圍 +' + pct(1.12, lv) + '%'; },
    mods: function (lv) { return { mul: { slamDmg: Math.pow(1.34, lv), slamArea: Math.pow(1.12, lv) } }; }
  },
  {
    /* 空中輸出預設 ×0.7，這條把它買回來 —— **上限就是 1.0，不會超過地面**。
       讓它超過的話「飛著打」變成無條件最優解，地面戰與地形設計整組貶值。 */
    id: 'p-airfire', n: '滯空射控', kind: '被動', axes: ['機動', '傷害'], max: 3,
    d: function (lv) { return '空中輸出懲罰減免 ' + Math.round(Math.min(1, lv / 3) * 100) + '%（滿級時空中輸出與地面相同）'; },
    mods: function (lv) { return { add: { airDmg: (1 - 0.7) * Math.min(1, lv / 3) } }; }
  }
];

/** 這一項最高幾級。規則類的離散效果刻意比較短。 */
export function passiveMax(p) { return (p && p.max) || 5; }

export function passiveById(id) {
  for (var i = 0; i < PASSIVES.length; i++) if (PASSIVES[i].id === id) return PASSIVES[i];
  return null;
}
